import { Paperclip, Type } from 'lucide-react';
import { useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Button } from '@avanta/shared/components/ui/Button';
import {
  CHAT_BUTTON_ACTIVE_CLASSNAME,
  CHAT_BUTTON_INPUT_CLASSNAME,
} from '@avanta/shared/constants/chat';
import { cn } from '@avanta/shared/utils/cn';
import { RichTextInput } from '../../../RichTextInput';
import { RecordButton } from '../RecordButton';
import { SendButton } from '../SendButton';
import { type FormValues, type MessageInputRowProps } from './types';

export function MessageInputRow({
  isRecording,
  isSending,
  isSubmitting,
  selectedFilesCount,
  leadingActions,
  onAttachClick,
  onMessageChange,
  onRecordClick,
}: MessageInputRowProps): React.ReactElement {
  const [isEditorGrown, setIsEditorGrown] = useState(false);
  const [isEditorEmpty, setIsEditorEmpty] = useState(true);
  const [isRichTextMode, setIsRichTextMode] = useState(false);
  const { control } = useFormContext<FormValues>();
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <Controller
      control={control}
      name="message"
      render={({ field }) => {
        const isValid = selectedFilesCount > 0 || !isEditorEmpty;

        const handleSubmit = (): void => {
          field.ref(null);
          const form = containerRef.current?.closest<HTMLFormElement>('form');
          form?.requestSubmit();
        };

        const handleHtmlChange = (html: string): void => {
          field.onChange(html);
          onMessageChange(html);
        };

        const handleEmptyChange = (isEmpty: boolean): void => {
          setIsEditorEmpty(isEmpty);
          setIsEditorGrown(!isEmpty || selectedFilesCount > 0);
        };

        return (
          <div
            ref={containerRef}
            className={cn(
              'flex gap-1 w-full justify-between',
              isEditorGrown ? 'flex-col items-end' : 'flex-row items-center'
            )}
          >
            <RichTextInput
              value={field.value ?? ''}
              onHtmlChange={handleHtmlChange}
              isRichTextMode={isRichTextMode}
              onEmptyChange={handleEmptyChange}
              onSubmit={handleSubmit}
            />
            <div className="flex">
              {leadingActions}
              <Button
                variant="ghost"
                className={cn(
                  CHAT_BUTTON_INPUT_CLASSNAME,
                  isRichTextMode && CHAT_BUTTON_ACTIVE_CLASSNAME
                )}
                icon="only"
                type="button"
                onClick={() => setIsRichTextMode(prev => !prev)}
              >
                <Type className="size-4.5" />
              </Button>
              <Button
                variant="ghost"
                className={CHAT_BUTTON_INPUT_CLASSNAME}
                icon="only"
                type="button"
                onClick={onAttachClick}
              >
                <Paperclip className="size-4.5" />
              </Button>
              {!isValid ? (
                <RecordButton
                  onClick={onRecordClick}
                  isRecording={isRecording}
                  isSending={isSending}
                />
              ) : (
                <SendButton disabled={isSubmitting} />
              )}
            </div>
          </div>
        );
      }}
    />
  );
}
